import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import * as d3 from 'd3';
import { useDispatch } from 'react-redux';
import { Grid, Card, CardContent, Box } from '@mui/material';
import { makeStyles, Theme } from '@material-ui/core/styles';

import FrontViewKeyMetrics from './FrontViewKeyMetrics';
import RearViewKeyMetrics from './RearViewKeyMetrics';
import { KeyMetricsCardPropTypes } from 'src/interfaces/iproptypes';
import { ADR, OccupancyRate, Revenue, ListingLegacy, Listing } from 'src/interfaces/idashboard';
import {
  getLTMData,
  formatCurrency,
  getMonthlyKeyMetrics,
  convertCurrency,
  // processLastMonthData,
  calculateMoMChange,
  isListingLegacy,
} from 'src/utils/functions';
import { exchangeRates, sampleMedianNightlyRateData } from 'src/const';
import { sampleOccupancyRateData, sampleRevenueData } from '../../../../const';
import {
  setAnnualRevenueDataForCalculator,
  setMaxSampleRevenuePotentialPropertySize,
} from 'src/store/Dashboard/actions';
import DemoFrontViewKeyMetricsCard from './DemoFrontViewKeyMetricsCard';

const useStyles = makeStyles((theme: Theme) => ({
  cardContainer: {
    height: '100%',
    transition: 'transform 0.5s',
  },
  cardContainerFlipped: {
    transform: 'rotateY( 180deg )',
    transition: 'transform 0.5s',
  },
  rear: {
    height: '100%',
    width: '100%',
    transform: 'rotateY(180deg)',
    transition: 'height 0.5s, transform 0.5s',
  },
}));

const getMetricsKey = (title: string) => {
  if (title.includes('Nightly Rate')) {
    return 'Nightly Rate';
  } else if (title.includes('Occupancy')) {
    return 'Occupancy Rate';
  } else {
    return 'Revenue';
  }
};

const getUnit = (titleVal: string, currency: string): string => {
  if (titleVal.includes('Occupancy')) {
    return '%';
  } else {
    return exchangeRates[currency].symbol;
  }
};

const KeyMetricsCard = ({
  title,
  listings,
  marketData,
  historicalMarketLevelData,
  color,
  tooltip,
  multipleMarkersCallLoading,
  isDisabled,
  currency,
  handleOnSampleClick,
  lastMonthData,
  historicalDataLink,
  renderIn,
  isUserAllowed,
  handleKpiCardView,
  svgLTMGraph,
  lockedMarketSvgGraphLoadingStatus,
  defaultSvgLtmGraphFallbackImage,
}: KeyMetricsCardPropTypes) => {
  const classes = useStyles();
  const metricsKey = getMetricsKey(title);
  let data: ADR[] | OccupancyRate[] | Revenue[] = [];
  const [percentile, setPercentile] = useState(0.5);
  // 6 -> Last 6 months, 12 -> Last 12 months, 100 -> All history
  const [timePeriod, setTimePeriod] = useState(12);

  const MARKET_DATA_MAPPING_AS_PER_PERCENTILE = {
    Revenue: {
      0.25: marketData['revenue_25'],
      0.5: marketData['revenue_50'],
      0.75: marketData['revenue_75'],
      0.9: marketData['revenue_90'],
    },
    'Occupancy Rate': {
      0.25: marketData['occupancy_25'],
      0.5: marketData['occupancy_50'],
      0.75: marketData['occupancy_75'],
      0.9: marketData['occupancy_90'],
    },
    'Nightly Rate': {
      0.25: marketData['adr_25'],
      0.5: marketData['adr_50'],
      0.75: marketData['adr_75'],
      0.9: marketData['adr_90'],
    },
  };

  let marketDataAsPerPercentile = MARKET_DATA_MAPPING_AS_PER_PERCENTILE[title][percentile]
    ? MARKET_DATA_MAPPING_AS_PER_PERCENTILE[title][percentile]
    : 0;

  let momChange: number | null = null;
  let fixedData;
  const [view, setView] = useState<'front' | 'rear'>('front');
  const [flip, setFlip] = useState<boolean>(false);
  const unit = getUnit(title, currency);

  useEffect(() => {
    if (handleKpiCardView) {
      handleKpiCardView(view, title);
    }
  }, [view]);

  const dispatch = useDispatch();

  useEffect(() => {
    if (title.includes('Revenue')) {
      dispatch(setAnnualRevenueDataForCalculator(marketDataAsPerPercentile));
    }
  }, [percentile]);

  const flipCard = (face: 'front' | 'rear') => {
    setFlip(!flip);
    // making it asynchronous
    setView(face);
  };

  useEffect(() => {
    if (title.includes('Revenue')) {
      dispatch(
        setMaxSampleRevenuePotentialPropertySize(
          convertCurrency(currency, marketDataAsPerPercentile)
        )
      );
    }
  }, [marketDataAsPerPercentile]);

  return (
    <Card
      sx={{
        overflow: 'visible !important',
      }}
    >
      <CardContent
        sx={{ minHeight: isUserAllowed ? 180 : 200, padding: '0px !important', margin: 0 }}
      >
        <Grid
          container
          className={clsx(classes.cardContainer, {
            [classes.cardContainerFlipped]: flip,
          })}
        >
          {view === 'front' && isUserAllowed && (
            <>
              <FrontViewKeyMetrics
                title={title}
                value={marketDataAsPerPercentile}
                tooltip={tooltip}
                color={color}
                isDisabled={isDisabled}
                handleOnSampleClick={handleOnSampleClick}
                flipCard={flipCard}
                hasLastMonthData={lastMonthData ? true : false}
                historicalDataLink={historicalDataLink}
                unit={unit}
                multipleMarkersCallLoading={multipleMarkersCallLoading}
                lastMonthData={lastMonthData}
                currency={currency}
                renderIn={renderIn}
                isUserAllowed={isUserAllowed}
              />
            </>
          )}
          {view === 'front' && !isUserAllowed && (
            <DemoFrontViewKeyMetricsCard
              title={title}
              value={marketDataAsPerPercentile}
              tooltip={tooltip}
              color={color}
              isDisabled={isDisabled}
              handleOnSampleClick={handleOnSampleClick}
              flipCard={flipCard}
              hasLastMonthData={lastMonthData ? true : false}
              historicalDataLink={historicalDataLink}
              unit={unit}
              multipleMarkersCallLoading={multipleMarkersCallLoading}
              lastMonthData={lastMonthData}
              currency={currency}
              renderIn={renderIn}
              isUserAllowed={isUserAllowed}
              svgLTMGraph={svgLTMGraph ? svgLTMGraph : null}
              lockedMarketSvgGraphLoadingStatus={
                lockedMarketSvgGraphLoadingStatus ? lockedMarketSvgGraphLoadingStatus : 'failed'
              }
              defaultSvgLtmGraphFallbackImage={
                defaultSvgLtmGraphFallbackImage ? defaultSvgLtmGraphFallbackImage : 'none'
              }
            />
          )}
          {view === 'rear' && (
            <Box className={classes.rear}>
              {/* <RearViewKeyMetrics
                title={title}
                data={data}
                aggregateData={marketDataAsPerPercentile}
                historicalMarketLevelData={historicalMarketLevelData}
                tooltip={tooltip}
                color={color}
                isDisabled={isDisabled}
                handleOnSampleClick={handleOnSampleClick}
                flipCard={flipCard}
                currency={currency}
                unit={unit}
                metricsKey={metricsKey}
                percentile={percentile}
                timePeriod={timePeriod}
                setPercentile={setPercentile}
                setTimePeriod={setTimePeriod}
                hasLastMonthData={lastMonthData ? true : false}
                momChange={momChange}
              /> */}
            </Box>
          )}
        </Grid>
      </CardContent>
    </Card>
  );
};

// export default KeyMetricsCard;
export default React.memo(KeyMetricsCard);
